import React from "react";
import { datadogRum } from "@datadog/browser-rum";
import { configureStore as configureRTKStore } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import Cookies from "js-cookie";
import _get from "lodash/get";
import { hydrateRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";
import { Reducer } from "redux";
import promiseMiddleware from "redux-promise-middleware";
import asyncErrorHandlingMiddleware from "./state/asyncErrorHandlingMiddleware";
import sentryMiddleware from "./state/sentryMiddleware";
import { RSWindow } from "../../../models";
import * as reducers from "../../js/reducers/index";
import checkoutMiddleware from "../checkout/state/middleware";
import * as FWA_ACTION_TYPES from "../fwa/state/actionTypes";
import { googleAnalyticsMiddleware } from "../google-analytics";
import loadingMiddleware from "../loading/state/middleware";
import AppTheme from "./components/AppTheme/AppTheme";
import { AppRoot, ErrorRoot, configActions } from ".";

const typedWindow: RSWindow = window;

const initialState = Object.assign({}, typedWindow.__INITIAL_STATE, {
    config: typedWindow.nettbutikkConfig,
});

const environment = _get(initialState, "config.environment", "");
const isDevMode = _get(initialState, "config.devMode", false);
const release = _get(initialState, "config.release");
const enableDatadogRUM = _get(initialState, "config.featureToggles.enableDatadogRUM", false);
const orderRef = Cookies.get("orderRef");

Sentry.init({
    dsn: "https://26bb94d39db54e798f27e92e9f0a6c99:30be28ee6701418b814a9dad88b841e3@sentry.io/101485",
    integrations: [Sentry.browserTracingIntegration()],
    environment: isDevMode ? "local" : environment,
    enabled: !isDevMode,
    release: release,
    initialScope: {
        user: { id: orderRef },
    },
});

if (!isDevMode && environment === "production" && enableDatadogRUM) {
    datadogRum.init({
        applicationId: "e1dcc632-e788-4d8f-b0d3-9e8245af321e",
        clientToken: "pub170d12b47f6e7bb91a0b98c9f3bbafb4",
        site: "datadoghq.eu",
        service: "telia-no-webshop",
        env: environment,
        // Specify a version number to identify the deployed version of your application in Datadog
        version: release,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 0,
        trackUserInteractions: true,
        trackResources: false,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask",
        beforeSend: (event) => {
            // discard all the errors since we handle those in other systems
            if (event.type === "error") {
                return false;
            }
            if (event.view.url === "https://www.telia.no/webshop-next/login-silent/") {
                return false;
            }
            return true;
        },
    });
    datadogRum.setGlobalContextProperty("h2id", "hid100005609");
    datadogRum.setGlobalContextProperty("billing-code", "p1-00177-01574");
    datadogRum.setGlobalContextProperty("business-unit", "cps");
    datadogRum.stopSessionReplayRecording();
} else {
    // oxlint-disable-next-line no-console
    console.warn("Did not start DatadogRum", { environment });
}

const configureStore = (preloadedState: typeof initialState, rootReducer: Reducer) => {
    const store = configureRTKStore({
        reducer: rootReducer,
        preloadedState,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: {
                    // Ignore Promise payloads for specific FWA actions handled by redux-promise-middleware
                    // The promise middleware transforms actions with Promise payloads into
                    // _PENDING, _FULFILLED, and _REJECTED actions before they reach reducers
                    ignoredActions: [
                        FWA_ACTION_TYPES.FWA_LOOKUP_PACKAGES,
                        `${FWA_ACTION_TYPES.FWA_LOOKUP_PACKAGES}_PENDING`,
                        `${FWA_ACTION_TYPES.FWA_LOOKUP_PACKAGES}_FULFILLED`,
                        `${FWA_ACTION_TYPES.FWA_LOOKUP_PACKAGES}_REJECTED`,
                    ],
                    // Also ignore meta.arg which may contain non-serializable values
                    ignoredPaths: ["meta.arg"],
                },
            }).concat(
                // Add custom middleware after default middleware (which includes thunk)
                promiseMiddleware,
                asyncErrorHandlingMiddleware(),
                googleAnalyticsMiddleware(),
                loadingMiddleware(),
                checkoutMiddleware(),
                sentryMiddleware(),
            ),
        devTools: true,
    });
    return store;
};

//
// Universal Redux component
//
const reactAppElement = document.getElementById("react-app");
if (reactAppElement) {
    const isError = reactAppElement.getAttribute("data-error") === "true";
    const reactReducerName = reactAppElement.getAttribute("data-react-reducer") as keyof typeof reducers;
    const reducer = reactReducerName ? reducers[reactReducerName] : reducers["checkout"];
    const store = configureStore(initialState, reducer);

    const initialOrderRef = store.getState().general?.orderRef ?? orderRef;
    if (initialOrderRef) {
        Sentry.setTag("orderRef", initialOrderRef);
        Sentry.setUser({ id: initialOrderRef });
    }
    const initialOrderType = store.getState().general?.orderType;
    if (initialOrderType) {
        Sentry.setTag("orderType", initialOrderType);
    }

    store.dispatch(configActions.initializeData(typedWindow.nettbutikkConfig));

    const Root = isError ? ErrorRoot : AppRoot;
    const componentWithStore = (
        <Provider store={store} serverState={initialState}>
            <Router>
                <AppTheme forceLight={isError}>
                    <Root />
                </AppTheme>
            </Router>
        </Provider>
    );

    hydrateRoot(reactAppElement, componentWithStore);
}
